/**
 * D2 station: G1–G4 gate screen + role dashboard figures (basis always shown).
 * Offline: enqueue to IndexedDB outbox; pending never shown as accepted.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { currentSession, scopeKey } from '../capture/deviceScope';
import GateScreen from './GateScreen';
import { blockedLegacyCount, displayStatus, enqueue, listPending, replayAll } from '../offline/outbox';
import { apiBase, authHeaders } from '../utils/authHeaders';
import { apiAllowed, useExperience } from '../experience/experience';

const API = apiBase();

function scanHeaders(scope) {
  return authHeaders({ orgId: scope.orgId, locationId: scope.locationId, token: scope.token, json: true });
}

function actionForGate(g) {
  if (g === 'G1') return 'receive';
  if (g === 'G2') return 'putaway';
  if (g === 'G3') return 'issue';
  return 'produce';
}

async function postCommands(commands, scope) {
  const r = await fetch(`${API}/api/scan/commands`, {
    method: 'POST',
    headers: scanHeaders(scope),
    body: JSON.stringify({ commands }),
  });
  const body = await r.json().catch(() => ({}));
  return { status: r.status, body, ok: r.ok };
}

const GATE_LABELS = {
  G1: 'G1 · Receive',
  G2: 'G2 · Put away',
  G3: 'G3 · Issue to batch',
  G4: 'G4 · Produce',
};

export default function ScanStationPage({ scope = {} }) {
  const session = useRef(scope);
  session.current = scope;
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const isCurrent = useCallback(() => alive.current && currentSession(scope)
    && scopeKey(scope) === scopeKey(session.current) && scope.token === session.current.token,
  [scope.userId, scope.orgId, scope.locationId, scope.token]); // eslint-disable-line react-hooks/exhaustive-deps
  const replaying = useRef(false);
  const [blocked, setBlocked] = useState(0);
  const exp = useExperience();
  // This card renders CK-manager figures only. Staff/viewers can scan without probing it.
  // Match scan_gates.dashboards.for_roles; never infer permission while experience is loading.
  const canReadDashboard = apiAllowed(exp, '/api/scan/dashboards/ck_manager')
    && (exp.roles || []).some((role) => ['org_owner', 'org_admin', 'manager'].includes(role))
    && !(exp.roles || []).some((role) => ['driver', 'api_client'].includes(role));
  const organizationId = exp.organizationId;
  const [gate, setGate] = useState('G1');
  const [recent, setRecent] = useState([]);
  const [dash, setDash] = useState(null);
  const [error, setError] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPending = useCallback(async () => {
    try {
      if (typeof indexedDB === 'undefined') return;
      const rows = await listPending(scope);
      const legacy = await blockedLegacyCount();
      if (!isCurrent()) return;
      setBlocked(legacy);
      setPendingCount(rows.filter((r) => r.status === 'pending').length);
      setRecent((prev) => {
        const pendingRows = rows
          .filter((r) => r.status === 'pending')
          .map((r) => ({
            command_id: r.command_id,
            code: r.code,
            result: displayStatus(r),
            pending: true,
            status: 'pending',
          }));
        const online = prev.filter((r) => !r.pending);
        return [...pendingRows, ...online].slice(0, 20);
      });
    } catch {
      /* IndexedDB unavailable (SSR / private mode) */
    }
  }, [isCurrent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setDash(null);
    if (!canReadDashboard || !isCurrent()) return undefined;
    (async () => {
      try {
        const r = await fetch(`${API}/api/scan/dashboards/ck_manager`, {
          headers: scanHeaders(scope),
        });
        if (!r.ok) return;
        const body = await r.json();
        if (!cancelled && isCurrent()) setDash(body);
      } catch {
        /* offline / module off */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canReadDashboard, organizationId, isCurrent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refreshPending(); }, [refreshPending]);

  useEffect(() => {
    const onOnline = async () => {
      if (!isCurrent() || replaying.current) return;
      replaying.current = true;
      try {
        await replayAll(async (row) => {
          const { status, body } = await postCommands([
            {
              command_id: row.command_id,
              payload_hash: row.payload_hash,
              gate: row.gate,
              action: row.action,
              code: row.code,
              qty: row.qty,
              uom: row.uom,
              ref: row.ref,
              captured_offline: true,
            },
          ], scope);
          return { status, body, httpStatus: status };
        }, { scope, isCurrent });
        await refreshPending();
      } catch {
        /* keep pending */
      } finally { replaying.current = false; }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refreshPending, isCurrent]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmitScan = useCallback(
    async ({ gate: g, code }) => {
      if (!isCurrent()) { setError('Session or site changed. Reopen Scan station.'); return { result: 'refused' }; }
      setError('');
      const command_id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const payload_hash = `h-${command_id}`.slice(0, 32).padEnd(8, 'x');
      const command = {
        command_id,
        payload_hash,
        gate: g,
        action: actionForGate(g),
        code,
      };

      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        try {
          await enqueue(command, scope);
          await refreshPending();
          return { result: 'PENDING', pending: true, code, command_id, status: 'pending' };
        } catch (e) {
          setError('Could not queue offline scan');
          return { result: 'refused', pending: false, code, command_id };
        }
      }

      try {
        const { ok, status, body } = await postCommands([command], scope);
        if (!isCurrent()) return { result: 'refused', code, command_id };
        if (!ok) {
          if (status >= 500 || status === 0) {
            await enqueue(command, scope);
            await refreshPending();
            return { result: 'PENDING', pending: true, code, command_id, status: 'pending' };
          }
          setError(body.detail || `Scan failed (${status})`);
          return { result: 'refused', pending: false, code, command_id };
        }
        const event = (body.events && body.events[0]) || { result: 'accepted', code, command_id };
        setRecent((prev) => [{ ...event, code, command_id, pending: false }, ...prev].slice(0, 20));
        return event;
      } catch {
        if (!isCurrent()) return { result: 'refused', code, command_id };
        try {
          await enqueue(command, scope);
          await refreshPending();
          return { result: 'PENDING', pending: true, code, command_id, status: 'pending' };
        } catch {
          setError('Network error — could not queue scan');
          return { result: 'refused', pending: false, code, command_id };
        }
      }
    },
    [refreshPending, isCurrent], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="p-4" data-testid="scan-station-page">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h1 className="text-xl font-semibold">Scan station</h1>
        <nav className="text-sm text-ink-muted flex flex-wrap gap-3" aria-label="Scan station quick links">
          <a href="/m/today" className="underline hover:text-primary-600">Inbound deliveries</a>
          <a href="/receiving/putaway" className="underline hover:text-primary-600">Putaway</a>
          <a href="/ck-ops" className="underline hover:text-primary-600">CK operations</a>
          <a href="/waste" className="underline hover:text-primary-600">Waste</a>
        </nav>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="scan-gate-picker">
        {['G1', 'G2', 'G3', 'G4'].map((g) => (
          <button
            key={g}
            type="button"
            data-testid={`scan-gate-${g}`}
            className={`min-h-[44px] px-4 py-2 rounded-full border text-sm ${
              gate === g
                ? 'bg-primary-500 text-surface border-primary-500 font-bold underline'
                : 'bg-surface text-ink border-surface-border hover:bg-primary-50'
            }`}
            onClick={() => setGate(g)}
          >
            {GATE_LABELS[g] || g}
          </button>
        ))}
      </div>
      {!currentSession(scope) && <p role="alert" data-testid="scan-scope-blocked">Scanning and replay are blocked until an authenticated user, organization and site are available. Open the signed-in desktop Scan station or sign in again.</p>}
      {blocked > 0 && <p role="alert" data-testid="scan-legacy-blocked">{blocked} legacy offline scan(s) blocked: original actor/site unknown. Not replayed. Ask an administrator to reconcile this device; do not reassign these rows.</p>}
      {pendingCount > 0 && (
        <p data-testid="scan-pending-banner" className="mb-2 text-sm">
          {pendingCount} offline scan(s) pending — not accepted until replay succeeds
        </p>
      )}
      {canReadDashboard && dash && (
        <aside data-testid="scan-dash-ck" className="mb-4 text-sm">
          <p>
            Open holds:{' '}
            <strong>{dash.exceptions_open?.value}</strong>
            <span className="opacity-70"> · {dash.exceptions_open?.basis}</span>
            <span className="opacity-70"> · {dash.exceptions_open?.period}</span>
          </p>
          <p>
            Batches in progress:{' '}
            <strong>{dash.batches_in_progress?.value}</strong>
            <span className="opacity-70"> · {dash.batches_in_progress?.basis}</span>
          </p>
        </aside>
      )}
      {error && (
        <p role="alert" data-testid="scan-station-error">
          {error}
        </p>
      )}
      {gate !== 'G2' && (
        // G1/G3/G4 record the scan for traceability but change no stock yet (backend scan_gates/ledger.py, D2).
        <p data-testid="scan-record-only" className="mb-3 text-sm rounded-md bg-warn/10 border border-warn/40 px-3 py-2">
          {gate === 'G1' ? 'Receive' : gate === 'G3' ? 'Issue to batch' : 'Produce'} scans are recorded for
          traceability but do not change stock here. Do it in{' '}
          {gate === 'G1' ? <a href="/m/today" className="underline">Inbound deliveries</a>
            : <a href="/ck-ops" className="underline">CK operations</a>}.
        </p>
      )}
      <GateScreen key={scopeKey(scope)} gate={gate} onSubmitScan={onSubmitScan} recent={recent} />
    </div>
  );
}
